import React, { useEffect, useRef, useState } from 'react';
import { CalendarPicker, LocalizationProvider, PickersDay } from '@mui/lab';
import AdapterMoment from '@mui/lab/AdapterMoment';
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  Stack,
  TextFieldProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  ViewWeek as ViewWeekIcon,
  ViewDay as ViewDayIcon,
  ViewAgenda as ViewAgendaIcon,
  SvgIconComponent,
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import {
  DatesSetArg,
  EventClickArg,
  EventSourceInput,
} from '@fullcalendar/core';
import moment, { Moment } from 'moment';

import CalendarStyle from './CalendarStyle';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import CalendarHeaderToolBar from './CalendarHeaderToolBar';
import AvailabilityModal from './AvailabilityModal';
import UserDetailCard from './UserDetailCard';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../components/Inputs';
import ViewEventModal from './ViewEventModel';
import { TP_COLORS } from '../../../constants';

export type TViewOption = {
  label: string;
  value: 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek';
  icon: SvgIconComponent;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialEvents: EventSourceInput = [
  {
    start: moment('09:00 AM', 'hh:mm A').add(1, 'day').toISOString(),
    end: moment('10:00 AM', 'hh:mm A').add(1, 'day').toISOString(),
    title: 'Lorem Ipsum Dolor.',
    backgroundColor: 'rgba(1, 167, 159, 0.3)',
    textColor: '#1E293B',
    borderColor: 'rgba(1, 167, 159, 0.3)',
    extendedProps: {
      id: new Date().getTime(),
      test: 'hello',
    },
  },
];

export const VIEW_OPTIONS: TViewOption[] = [
  { value: 'dayGridMonth', label: 'Month', icon: CalendarMonthIcon },
  { value: 'timeGridWeek', label: 'Week', icon: ViewWeekIcon },
  { value: 'timeGridDay', label: 'Day', icon: ViewDayIcon },
  { value: 'listWeek', label: 'Agenda', icon: ViewAgendaIcon },
];

const Schedule: React.FC = () => {
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const calendarRef = useRef<FullCalendar | null>(null);

  // State
  const [calendarEvents, setCalendarEvents] = useState([...initialEvents]);
  const [calendarDate, setCalendarDate] = useState(moment());
  const [isAvailabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [calendarView, setCalendarView] = useState<TViewOption>(
    VIEW_OPTIONS[1]
  );

  const [viewEventModalData, setViewEventModalData] = useState<{
    open: boolean;
    arg?: EventClickArg;
  }>({
    open: false,
  });

  const calendarApi = calendarRef.current?.getApi();

  useEffect(() => {
    if (isXsScreen && calendarApi) {
      calendarApi.changeView(VIEW_OPTIONS[3].value);
      setCalendarView(VIEW_OPTIONS[3]);
    }
  }, [isXsScreen]);

  const handleDateSet = (arg: DatesSetArg) => {
    setCalendarDate(moment(arg.start));
  };

  const handleClickDateNext = () => {
    if (calendarApi) {
      calendarApi.next();
    }
  };

  const handleClickDatePrev = () => {
    if (calendarApi) {
      calendarApi.prev();
    }
  };

  const handleClickToday = () => {
    if (calendarApi) {
      calendarApi.today();
    }
  };

  const handleChangeView = (viewOption: TViewOption) => {
    if (calendarApi) {
      calendarApi.changeView(viewOption.value);
      setCalendarView(viewOption);
    }
  };

  const toggleAvailabilityModal = () => {
    setAvailabilityModalOpen(!isAvailabilityModalOpen);
  };

  const handleCalendarPickerDateChange = (newDate: Moment | null) => {
    if (newDate && calendarApi) {
      setCalendarDate(newDate);
      calendarApi.gotoDate(newDate.toDate());
    }
  };

  const handleEventClick = (arg: EventClickArg) => {
    setViewEventModalData({
      open: true,
      arg,
    });
  };

  const closeViewEventModal = () => {
    setViewEventModalData({
      open: false,
    });
  };

  const constructBreadcrumbOptions = () => {
    const options: Array<BreadcrumbOption> = [
      {
        type: 'link',
        title: 'Home',
        link: `${process.env.PUBLIC_URL}`,
      },
      {
        type: 'text',
        title: 'Schedule',
        highlight: true,
      },
    ];

    return options;
  };

  const renderWeekPickerDay = (day, selectedDates, pickersDayProps) => {
    if (calendarView.value === 'timeGridDay') {
      return <PickersDay {...pickersDayProps} />;
    }

    let start = calendarDate.clone().startOf('week');
    let end = calendarDate.clone().endOf('week');

    if (calendarView.value === 'dayGridMonth') {
      start = calendarDate.clone().add(15, 'days').startOf('month');
      end = start.clone().endOf('month');
    }

    const dayIsBetween = day.isSameOrAfter(start) && day.isSameOrBefore(end);
    const isFirstDay = day.isSame(start, 'day');
    const isLastDay = day.isSame(end, 'day');
    const isSelected =
      day.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');

    const _styles = {
      ...pickersDayProps.style,
      borderRadius: 0,
    };

    if (dayIsBetween) {
      _styles.backgroundColor = '#F1F5F9';
    }

    if (isFirstDay) {
      _styles.borderTopLeftRadius = '50%';
      _styles.borderBottomLeftRadius = '50%';
    }

    if (isLastDay) {
      _styles.borderTopRightRadius = '50%';
      _styles.borderBottomRightRadius = '50%';
    }

    if (isSelected && !dayIsBetween && !isFirstDay && !isLastDay) {
      delete _styles.borderRadius;
    }

    return (
      <PickersDay
        {...pickersDayProps}
        day={day}
        disableMargin
        selected={isSelected}
        sx={_styles}
      />
    );
  };

  const breadcrumbOptions = constructBreadcrumbOptions();

  return (
    <Container maxWidth="xl">
      <div style={{ padding: '30px 0' }}>
        <Breadcrumbs
          options={breadcrumbOptions}
          textColor="#647488"
          showDropDown={isXsScreen}
        />
      </div>
      <Stack direction="row" justifyContent="flex-end" mb="15px">
        <Button
          color="inherit"
          variant="text"
          onClick={toggleAvailabilityModal}
          sx={{ fontWeight: 600, textDecoration: 'underline' }}
        >
          Set Availability
        </Button>
      </Stack>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={3} xl={3}>
            <Grid
              container
              spacing={2}
              sx={isDesktop ? { position: 'sticky', top: '64px' } : undefined}
            >
              <Grid item xs={12} sm={12} lg={12}>
                <div style={{ maxWidth: isDesktop ? undefined : '300px' }}>
                  <UserDetailCard />
                </div>
              </Grid>
              <Grid item xs={12} sm={4} lg={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={null}
                    options={[]}
                    // onChange={handleCurriculumChange}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    // loading={curriculumState.status === 'loading'}
                    // isOptionEqualToValue={(option, value) =>
                    //   option.label === value.value
                    // }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        label="User"
                        placeholder="User"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} lg={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={null}
                    options={[]}
                    // onChange={handleCurriculumChange}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    // loading={curriculumState.status === 'loading'}
                    // isOptionEqualToValue={(option, value) =>
                    //   option.label === value.value
                    // }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        label="Course"
                        placeholder="Course"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} lg={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={null}
                    options={[]}
                    // onChange={handleCurriculumChange}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    // loading={curriculumState.status === 'loading'}
                    // isOptionEqualToValue={(option, value) =>
                    //   option.label === value.value
                    // }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        size="small"
                        label="Timezone"
                        placeholder="Timezone"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              {isDesktop ? (
                <Grid item xs={12}>
                  <Stack alignItems="center" flexGrow={1}>
                    <div
                      style={{
                        backgroundColor: TP_COLORS.white,
                        borderRadius: '20px',
                        boxShadow: '0px 14px 40px 0px #6E9DB22E',
                        maxWidth: '320px',
                      }}
                    >
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <CalendarPicker
                          date={
                            calendarView.value === 'dayGridMonth'
                              ? calendarDate.clone().add(15, 'days')
                              : calendarDate
                          }
                          onChange={handleCalendarPickerDateChange}
                          renderDay={renderWeekPickerDay}
                        />
                      </LocalizationProvider>
                    </div>
                  </Stack>
                </Grid>
              ) : null}
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={9} xl={9}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '20px',
                boxShadow: '0px 14px 40px 0px #6E9DB22E',
              }}
            >
              <CardContent sx={{ p: '16px' }}>
                <CalendarStyle>
                  <CalendarHeaderToolBar
                    title={calendarApi?.view.title || ''}
                    view={calendarView}
                    onNextDate={handleClickDateNext}
                    onPrevDate={handleClickDatePrev}
                    onToday={handleClickToday}
                    onChangeView={handleChangeView}
                  />
                  <FullCalendar
                    allDayMaintainDuration
                    eventDisplay="block"
                    headerToolbar={false}
                    events={calendarEvents}
                    ref={calendarRef}
                    initialDate={calendarDate.toDate()}
                    initialView={calendarView.value}
                    datesSet={handleDateSet}
                    eventClick={handleEventClick}
                    height={
                      calendarView.value === 'dayGridMonth' ||
                      calendarView.value === 'listWeek'
                        ? undefined
                        : 'auto'
                    }
                    plugins={[listPlugin, dayGridPlugin, timeGridPlugin]}
                  />
                </CalendarStyle>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
      {/* Modals */}
      {isAvailabilityModalOpen ? (
        <AvailabilityModal
          open={isAvailabilityModalOpen}
          onClose={toggleAvailabilityModal}
        />
      ) : null}
      <ViewEventModal
        open={viewEventModalData.open}
        onClose={closeViewEventModal}
      />
    </Container>
  );
};

export default Schedule;
